<template>
  <div class="cell" :class="{ 'full-width': fullWidth }" @click.stop>
    <span ref="trigger" class="trigger">
      <slot
        name="button"
        :isOpen="isOpen"
        :toggle="toggle"
        :open="open"
        :close="close"
      ></slot>
    </span>
    <teleport v-if="menuMounted" to="body">
      <transition name="menu-anim" mode="out-in" appear @after-leave="menuMounted = isOpen">
        <div
          class="menu"
          v-if="isOpen"
          ref="menu"
          tabindex="-1"
          @click.stop
          :style="menuStyle"
        >
          <slot name="menu" :close="close"></slot>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import { markRaw } from "vue";

export default {
  name: "AbsoluteMenu",
  emits: ["close", "open"],
  props: {
    rightSide: { type: Boolean, default: false },
    closeOnOutsideClick: { type: Boolean, default: true },
    matchTriggerWidth: { type: Boolean, default: false },
    maxTriggerWidth: { type: Boolean, default: false },
    fullWidth: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      menuMounted: false,
      nestedMenus: [],
      instanceId: Math.random().toString(36).slice(2),
      menuTop: 0,
      menuLeft: 0,
      menuMaxHeight: null,
      menuWidth: 0,
      alignRight: false,
    };
  },
  computed: {
    menuStyle() {
      const base = {
        position: "fixed",
        top: `${this.menuTop}px`,
        zIndex: 99999,
      };

      if (this.menuMaxHeight != null) {
        base.maxHeight = `${this.menuMaxHeight}px`;
        base.overflowY = "auto";
      }

      if (this.matchTriggerWidth && this.menuWidth) {
        base.minWidth = `${this.menuWidth}px`;
      }

      if (this.maxTriggerWidth && this.menuWidth) {
        base.maxWidth = `${this.menuWidth}px`;
      }

      if (this.alignRight) {
        const right = Math.max(8, window.innerWidth - this.menuLeft);
        return { ...base, right: `${right}px` };
      }

      return { ...base, left: `${this.menuLeft}px` };
    },
  },
  methods: {
    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },
    open() {
      this.menuMounted = true;
      this.isOpen = true;
      this.$emit("open");
      this.notifyOpened();
      this.updateMenuPosition();
      this.$nextTick(() => {
        this.updateMenuPosition();
        this.focusAndScrollMenu();
      });
    },
    close(restoreFocus = false) {
      const wasOpen = this.isOpen;
      this.isOpen = false;
      this.nestedMenus = [];
      this.menuMaxHeight = null;
      this.removePositionListeners();
      if (!wasOpen) return;

      this.$emit("close");
      if (restoreFocus) this.$nextTick(() => this.focusTrigger());
    },
    focusTrigger() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;

      const target = trigger.querySelector('button, [tabindex]:not([tabindex="-1"]), input, select');
      target?.focus();
    },
    notifyOpened() {
      window.dispatchEvent(
          new CustomEvent("absolute-menu-open", {
            detail: { instanceId: this.instanceId, instance: this },
          })
      );
    },
    onEscape(event) {
      if (!this.isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
        return;
      }

      if (event.key === "Tab") this.trapTab(event);
    },
    trapTab(event) {
      const root = this.$refs.menu;
      if (!root) return;

      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ');

      const items = Array.from(root.querySelectorAll(selector)).filter((item) => item.offsetParent !== null);

      event.preventDefault();
      event.stopPropagation();

      if (!items.length) {
        root.focus();
        return;
      }

      const active = document.activeElement;
      const index = items.indexOf(active);
      const step = event.shiftKey ? -1 : 1;
      const next = index === -1 ? 0 : (index + step + items.length) % items.length;
      items[next].focus();
    },
    onOtherMenuOpened(e) {
      const opened = e?.detail?.instance;
      if (!opened || opened === this) return;

      if (this.ownsNode(opened.$refs?.trigger)) {
        if (!this.nestedMenus.includes(opened)) this.nestedMenus.push(markRaw(opened));
        return;
      }
      this.close();
    },
    ownsNode(node) {
      if (!node) return false;
      const menuEl = this.$refs.menu;
      if (menuEl && menuEl.contains(node)) return true;
      return this.nestedMenus.some((menu) => menu.ownsNode(node));
    },
    updateMenuPosition() {
      const triggerEl = this.$refs.trigger;
      if (!triggerEl || typeof triggerEl.getBoundingClientRect !== "function") return;

      const rect = triggerEl.getBoundingClientRect();
      const gap = 10;
      const margin = 8;
      const viewportHeight = window.innerHeight;

      let left = rect.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - margin);

      // scrollHeight plutot que offsetHeight : reste la hauteur naturelle meme une fois maxHeight applique,
      // sinon la mesure se mordrait la queue a chaque scroll.
      const menuEl = this.$refs.menu;
      const menuHeight = menuEl ? menuEl.scrollHeight : 0;

      const spaceBelow = viewportHeight - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;

      // Pas la place en dessous et plus de place au-dessus : on ouvre vers le haut.
      const flipUp = menuHeight > 0 && menuHeight > spaceBelow && spaceAbove > spaceBelow;

      let top;
      if (flipUp) {
        top = Math.max(margin, rect.top - gap - menuHeight);
        this.menuMaxHeight = Math.max(0, spaceAbove);
      } else {
        top = rect.bottom + gap;
        this.menuMaxHeight = menuHeight > 0 ? Math.max(0, spaceBelow) : null;
      }

      const contentWidth = menuEl ? Math.max(menuEl.offsetWidth, menuEl.scrollWidth) : 0;
      const overflowsRight = contentWidth > 0 && left + contentWidth > window.innerWidth - margin;
      const overflowsLeft = contentWidth > 0 && rect.right - contentWidth < margin;
      this.alignRight = this.rightSide ? !overflowsLeft : overflowsRight && !overflowsLeft;

      this.menuWidth = rect.width;
      this.menuLeft = this.alignRight ? rect.right : left;
      this.menuTop = Math.min(Math.max(margin, top), viewportHeight - margin);
    },
    focusAndScrollMenu() {
      const menuEl = this.$refs.menu;
      if (!menuEl) return;

      if (typeof menuEl.scrollIntoView === "function") {
        menuEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }

      if (typeof menuEl.focus === "function") {
        try {
          menuEl.focus({ preventScroll: true });
        } catch (e) {
          menuEl.focus();
        }
      }
    },

    onDocumentClick(e) {
      if (!this.closeOnOutsideClick || !this.isOpen) return;
      const triggerEl = this.$refs.trigger;
      const menuEl = this.$refs.menu;
      const target = e.target;
      if ((triggerEl && triggerEl.contains(target)) || (menuEl && menuEl.contains(target))) {
        return;
      }
      if (this.nestedMenus.some((menu) => menu.ownsNode(target))) {
        return;
      }
      const openNested = this.nestedMenus.filter((menu) => menu.isOpen);
      if (openNested.length) {
        openNested.forEach((menu) => menu.close());
        return;
      }
      this.close();
    },
    addOpenListeners() {
      document.addEventListener("click", this.onDocumentClick, true);
      window.addEventListener("keydown", this.onEscape, true);
      window.addEventListener("absolute-menu-open", this.onOtherMenuOpened);
    },
    removeOpenListeners() {
      document.removeEventListener("click", this.onDocumentClick, true);
      window.removeEventListener("keydown", this.onEscape, true);
      window.removeEventListener("absolute-menu-open", this.onOtherMenuOpened);
    },
    addPositionListeners() {
      window.addEventListener("scroll", this.onPositionUpdate, true);
      window.addEventListener("resize", this.onPositionUpdate);
    },
    removePositionListeners() {
      window.removeEventListener("scroll", this.onPositionUpdate, true);
      window.removeEventListener("resize", this.onPositionUpdate);
    },
    onPositionUpdate() {
      if (!this.isOpen) return;
      this.updateMenuPosition();
    },
  },
  mounted() {
    this.onPositionUpdate = this.onPositionUpdate.bind(this);
    this.$watch(
      () => this.isOpen,
      (v) => {
        if (v) {
          this.addOpenListeners();
          this.addPositionListeners();
        } else {
          this.removeOpenListeners();
          this.removePositionListeners();
        }
      }
    );
  },
  beforeUnmount() {
    this.removeOpenListeners();
    this.removePositionListeners();
  },
};
</script>

<style scoped lang="scss">
.cell {
  position: relative;

  &.full-width {
    width: 100%;

    .trigger {
      display: flex;
      width: 100%;
    }
  }
}
.trigger {
  display: inline-flex;
}

.menu {
  transition: opacity 0.16s ease, transform 0.16s ease !important;
  z-index: $z-menu;
  min-width: 100px;
  width: fit-content;
  display: flex;
  flex-direction: column;
  gap: $default-edge-spacing-xs;
  padding: $default-edge-spacing-s;
  background-color: $color-input-back-darker-solid;
  border: 1px solid $color-separator;
  border-radius: 12px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  @include thin-scrollbar;

  &:focus {
    outline: none;
  }
}

.menu-anim-enter-from,
.menu-anim-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}
</style>